import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Search, Plus, Edit, Trash2, Package, LayoutGrid, List, X, RefreshCw, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { formatPrice } from '../../lib/utils';
import { cn } from '../../lib/utils';
import ImageUpload from '../../components/ImageUpload';

const CATEGORY_LABELS = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
  wordpress_support: 'WordPress Support',
  wordpress_upgrades: 'WordPress Upgrades',
  woocommerce_upgrades: 'WooCommerce Upgrades',
  newsletter: 'Newsletter',
  accessibility: 'Accessibility',
  hosting: 'Hosting',
  email_client: 'Email Client',
};

const CATEGORIES = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }));

const emptyService = {
  name: '',
  category: 'hosting',
  description: '',
  short_description: '',
  pricing_tiers: [{ name: '', price: 0, duration_months: 1, features: [] }],
  features: [],
  image_url: '',
  icon: '',
};

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState(emptyService);
  const [newFeature, setNewFeature] = useState('');
  const [expandedServices, setExpandedServices] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await adminAPI.getAllServicesAdmin();
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          service.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || service.category === filterCategory;
    const matchesActive = !showActiveOnly || service.is_active;
    return matchesSearch && matchesCategory && matchesActive;
  });

  const handleCreate = () => {
    setEditingService(null);
    setFormData({ ...emptyService, pricing_tiers: [{ name: '', price: 0, duration_months: 1, features: [] }] });
    setShowDialog(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      category: service.category,
      description: service.description,
      short_description: service.short_description,
      pricing_tiers: service.pricing_tiers || [],
      features: service.features || [],
      image_url: service.image_url || '',
      icon: service.icon || '',
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Service name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingService) {
        await adminAPI.updateService(editingService.id, formData);
        toast.success('Service updated');
      } else {
        await adminAPI.createService(formData);
        toast.success('Service created');
      }
      setShowDialog(false);
      loadServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save service');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (service) => {
    try {
      await adminAPI.updateService(service.id, { is_active: !service.is_active });
      toast.success(service.is_active ? 'Service deactivated' : 'Service activated');
      loadServices();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const addPricingTier = () => {
    setFormData({
      ...formData,
      pricing_tiers: [...formData.pricing_tiers, { name: '', price: 0, duration_months: 1, features: [] }],
    });
  };

  const removePricingTier = (index) => {
    setFormData({
      ...formData,
      pricing_tiers: formData.pricing_tiers.filter((_, i) => i !== index),
    });
  };

  const updatePricingTier = (index, field, value) => {
    const newTiers = [...formData.pricing_tiers];
    newTiers[index] = { ...newTiers[index], [field]: field === 'price' || field === 'duration_months' ? parseFloat(value) || 0 : value };
    setFormData({ ...formData, pricing_tiers: newTiers });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  const toggleServiceExpand = (serviceId) => {
    setExpandedServices(prev => ({ ...prev, [serviceId]: !prev[serviceId] }));
  };

  // Stats
  const totalServices = services.length;
  const activeServices = services.filter(s => s.is_active).length;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Services</h1>
            <p className="text-sm text-[#525252] mt-1">{totalServices} total, {activeServices} active</p>
          </div>
          <button 
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-[#262626] transition-all"
            data-testid="create-service-btn"
          >
            <Plus className="w-4 h-4" />
            Add Service
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#EBEBEB] rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" />
              <input
                type="text"
                placeholder="Search services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 pl-10 pr-4 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-[#0A0A0A]"
                data-testid="service-search-input"
              />
            </div>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="h-10 px-4 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-black/5"
              data-testid="service-category-filter"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>

            <label className="flex items-center gap-2 text-sm text-[#525252] cursor-pointer">
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                className="w-4 h-4 rounded border-[#E5E5E5]"
              />
              Active only
            </label>

            <div className="flex items-center bg-[#F5F5F5] rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  "p-2 rounded-md transition-all",
                  viewMode === 'list' ? "bg-white shadow-sm" : "hover:bg-white/50"
                )}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4 text-[#525252]" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  "p-2 rounded-md transition-all",
                  viewMode === 'grid' ? "bg-white shadow-sm" : "hover:bg-white/50"
                )}
                data-testid="view-grid-btn"
              >
                <LayoutGrid className="w-4 h-4 text-[#525252]" />
              </button>
            </div>
          </div>
        </div>

        {/* Services */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-black rounded-full animate-spin"></div>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="bg-white border border-stone-100 rounded p-8 text-center">
            <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-3">
              <Package className="w-5 h-5 text-stone-400" />
            </div>
            <h4 className="text-sm font-medium text-stone-900 mb-1">No Services Found</h4>
            <p className="text-xs text-stone-500 mb-4">
              {searchQuery || filterCategory ? 'Try adjusting your filters' : 'Get started by creating your first service'}
            </p>
            {!searchQuery && !filterCategory && (
              <button 
                onClick={handleCreate}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-stone-900 text-white rounded hover:bg-stone-800 transition-all"
              >
                <Plus className="w-3 h-3" />
                Create Service
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredServices.map((service) => (
              <div 
                key={service.id}
                className={cn(
                  "bg-white border border-stone-100 rounded p-4 hover:shadow-md transition-all",
                  !service.is_active && "opacity-60"
                )}
                data-testid={`service-card-${service.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{service.name}</p>
                    <span className="inline-block text-[9px] font-medium px-1.5 py-0.5 rounded bg-stone-100 text-stone-600 mt-1">
                      {CATEGORY_LABELS[service.category] || service.category}
                    </span>
                  </div>
                  <span className={cn(
                    "text-[9px] font-medium px-1.5 py-0.5 rounded-full",
                    service.is_active ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-500"
                  )}>
                    {service.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                <p className="text-[11px] text-stone-500 line-clamp-2 mb-3">{service.short_description}</p>
                
                <div className="flex flex-wrap gap-1 mb-3">
                  {service.pricing_tiers?.slice(0, 2).map((tier, idx) => (
                    <span key={idx} className="text-[10px] px-1.5 py-0.5 bg-stone-50 rounded">
                      <span className="text-stone-500">{tier.name}:</span>{' '}
                      <span className="font-semibold text-stone-900">{formatPrice(tier.price)}</span>
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-1 pt-3 border-t border-stone-100">
                  <button
                    onClick={() => handleEdit(service)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium text-stone-600 bg-stone-50 rounded hover:bg-stone-100 transition-all"
                    data-testid={`edit-service-${service.id}`}
                  >
                    <Edit className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleToggleActive(service)}
                    className={cn(
                      "px-2 py-1.5 text-[10px] font-medium rounded transition-all",
                      service.is_active ? "text-red-600 hover:bg-red-50" : "text-green-600 hover:bg-green-50"
                    )}
                  >
                    {service.is_active ? <Trash2 className="w-3 h-3" /> : <Check className="w-3 h-3" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-stone-100 rounded overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50">
                  <th className="text-left p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Service</th>
                  <th className="text-left p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Category</th>
                  <th className="text-left p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Pricing</th>
                  <th className="text-center p-3 text-[10px] font-semibold text-stone-500 uppercase tracking-wide">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {filteredServices.map((service) => (
                  <tr key={service.id} className={cn("hover:bg-stone-50 transition-colors", !service.is_active && "opacity-60")}>
                    <td className="p-3">
                      <p className="text-xs font-medium text-stone-900">{service.name}</p>
                      <p className="text-[10px] text-stone-500 line-clamp-1">{service.short_description}</p>
                    </td>
                    <td className="p-3">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">
                        {CATEGORY_LABELS[service.category] || service.category}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {service.pricing_tiers?.slice(0, 2).map((tier, idx) => (
                          <span key={idx} className="text-[10px] text-stone-600">
                            {tier.name}: <span className="font-semibold">{formatPrice(tier.price)}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={cn(
                        "inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full",
                        service.is_active ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-500"
                      )}>
                        {service.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(service)}
                          className="p-1.5 rounded hover:bg-stone-100 transition-colors"
                          data-testid={`edit-service-${service.id}`}
                        >
                          <Edit className="w-3.5 h-3.5 text-stone-400" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(service)}
                          className={cn(
                            "p-1.5 rounded transition-colors",
                            service.is_active ? "hover:bg-red-50 text-red-500" : "hover:bg-green-50 text-green-500"
                          )}
                        >
                          {service.is_active ? <Trash2 className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded w-full max-w-2xl max-h-[90vh] overflow-auto m-4">
              <div className="flex items-center justify-between p-4 border-b border-stone-100">
                <h3 className="text-sm font-semibold text-stone-900">
                  {editingService ? 'Edit Service' : 'Create Service'}
                </h3>
                <button onClick={() => setShowDialog(false)} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-4 h-4 text-stone-500" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full h-8 px-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                    placeholder="Service name"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-8 px-2 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Short Description */}
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Short Description</label>
                  <input
                    type="text"
                    value={formData.short_description}
                    onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                    className="w-full h-8 px-3 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400"
                    placeholder="Brief description"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Full Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 text-xs border border-stone-200 rounded focus:outline-none focus:border-stone-400 resize-none"
                    placeholder="Detailed description"
                  />
                </div>

                {/* Pricing Tiers */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[11px] font-medium text-stone-600">Pricing Tiers</label>
                    <button
                      type="button"
                      onClick={addPricingTier}
                      className="text-[10px] font-medium text-blue-600 hover:text-blue-700"
                    >
                      + Add Tier
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.pricing_tiers.map((tier, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-stone-50 rounded">
                        <input
                          type="text"
                          value={tier.name}
                          onChange={(e) => updatePricingTier(idx, 'name', e.target.value)}
                          className="flex-1 h-7 px-2 text-xs border border-stone-200 rounded"
                          placeholder="Tier name"
                        />
                        <input
                          type="number"
                          value={tier.price}
                          onChange={(e) => updatePricingTier(idx, 'price', e.target.value)}
                          className="w-24 h-7 px-2 text-xs border border-stone-200 rounded"
                          placeholder="Price"
                        />
                        <input
                          type="number"
                          value={tier.duration_months}
                          onChange={(e) => updatePricingTier(idx, 'duration_months', e.target.value)}
                          className="w-16 h-7 px-2 text-xs border border-stone-200 rounded"
                          placeholder="Months"
                        />
                        {formData.pricing_tiers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePricingTier(idx)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features */}
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-2">Features</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      className="flex-1 h-7 px-2 text-xs border border-stone-200 rounded"
                      placeholder="Add feature"
                    />
                    <button
                      type="button"
                      onClick={addFeature}
                      className="px-3 h-7 text-xs font-medium bg-stone-100 rounded hover:bg-stone-200"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {formData.features.map((feature, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 text-[10px] bg-stone-100 rounded">
                        {feature}
                        <button onClick={() => removeFeature(idx)} className="text-stone-400 hover:text-stone-600">
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div>
                  <label className="block text-[11px] font-medium text-stone-600 mb-1">Image</label>
                  <ImageUpload
                    value={formData.image_url}
                    onChange={(url) => setFormData({ ...formData, image_url: url })}
                    placeholder="Upload service image"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-4 border-t border-stone-100 bg-stone-50">
                <button
                  onClick={() => setShowDialog(false)}
                  className="px-4 py-2 text-xs font-medium text-stone-600 hover:bg-stone-100 rounded transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-4 py-2 text-xs font-medium text-white bg-stone-900 rounded hover:bg-stone-800 transition-all disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editingService ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
